<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { ModelRef } from "vue";
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { useSideNavigation } from "~/core/composables/useSideNavigation";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import { Filters } from "~/core/models/filters";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { useFetchUserGroups } from "~/modules/user/composables/useFetchUserGroups";
import { PATH_USER_MANAGEMENT_GROUPS } from "~/modules/user/constants";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import { UserGroupColumns } from "~/modules/user/models/UserGroupColumns.enum";

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { getSectorName } = useSectorStore();
const { can } = useAcl();
const { navigateToPath, onClose } = useSideNavigation();

const searchQuery = computed({
  get() {
    return (filters.get(Filters.NAME) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.NAME, searchQuery);
  },
});
const columns: Ref<UserGroupColumns[]> = ref<UserGroupColumns[]>([]);
const organizationCode = currentOrganization.value!.code;
const {
  isLoading,
  results: userGroups,
  fetchData,
  error,
  filters,
  setFilter,
  onSearch,
} = useFetchUserGroups({
  organizationCode: organizationCode,
  searchMode: SearchMode.FILTER_SEARCH,
});
const showGroupForm: ModelRef<boolean> = defineModel<boolean>("showGroupForm", {
  required: true,
});
const currentSelectedGroup: ModelRef<string | undefined> = defineModel<
  string | undefined
>("currentSelectedGroup");

/**
 * Retrieve userGroups
 * @param page
 * @param pageSize
 */
async function getUserGroups(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  await fetchData(page, pageSize);
  if (!error.value) {
    columns.value = [UserGroupColumns.NAME];
    const hasNbUsers: boolean | undefined = userGroups.value?.results.some(
      (userGroup: GroupsUserV2) => {
        return userGroup.nbUsers && userGroup.nbUsers >= 0;
      },
    );
    if (hasNbUsers) {
      columns.value.push(UserGroupColumns.NB_USERS);
    }
    const hasSectors: boolean | undefined = userGroups.value?.results.some(
      (userGroup: GroupsUserV2) => {
        return userGroup?.sectorCodes && userGroup.sectorCodes.length > 0;
      },
    );
    if (hasSectors) {
      columns.value.push(UserGroupColumns.SECTORS);
    }
  }
}

/**
 * Redirect when selecting à specific userGroup
 * @param userGroup
 */
async function onSelectUserGroup(userGroup: GroupsUserV2) {
  if (can(ACLRoles.CAN_WRITE_USER)) {
    currentSelectedGroup.value = userGroup.name;
    showGroupForm.value = true;
    await navigateToPath(`${PATH_USER_MANAGEMENT_GROUPS}/${userGroup.name}`);
  }
}

async function reloadWithContext() {
  await getUserGroups(
    userGroups.value?.pagination?.page,
    userGroups.value?.pagination?.pageSize,
  );
}

async function performSearch() {
  await getUserGroups(
    DEFAULT_FIRST_PAGE,
    userGroups.value?.pagination?.pageSize,
  );
}

function getSectorLabels(sectors?: number[]) {
  const res = (sectors || []).map(
    (sectorCode: number) => getSectorName(sectorCode) || sectorCode,
  );
  return res;
}

function onCloseCanvas(event: CloseEvent) {
  onClose(event, PATH_USER_MANAGEMENT_GROUPS, reloadWithContext);
}

onSearch(getUserGroups);
</script>

<template>
  <search-bar
    v-model:search-query="searchQuery"
    :search-placeholder="t('userGroup.search')"
    @clear="performSearch"
    @search="performSearch"
  />

  <container-component :px="0" :py="4">
    <lxc-table
      min-width="45rem"
      :is-loading="isLoading"
      :data="userGroups?.results"
      :empty-text="t('userGroup.empty')"
      :context="userGroups?.pagination"
      :error="error?.toError()"
      :clickable="can(ACLRoles.CAN_WRITE_USER)"
      class="mt-2"
      @row-click="onSelectUserGroup"
      @change-page-and-page-size="getUserGroups"
    >
      <lxc-table-column
        v-if="!columns || columns.includes(UserGroupColumns.NAME)"
        prop="name"
        :label="t('table.header.label')"
        min-width="15rem"
      />
      <lxc-table-column
        v-if="!columns || columns.includes(UserGroupColumns.SECTORS)"
        class="!py-0"
        :label="t('table.header.sectors')"
        min-width="12rem"
      >
        <template #default="scope">
          <tag-for-list
            :tags="getSectorLabels(scope.row.sectorCodes)"
            :max="2"
          />
        </template>
      </lxc-table-column>
      <lxc-table-column
        v-if="!columns || columns.includes(UserGroupColumns.NB_USERS)"
        align="right"
        prop="nbUsers"
        :label="t('table.header.nbUsers')"
        width="12rem"
      />
      <lxc-table-column
        v-if="can(ACLRoles.CAN_WRITE_USER)"
        class="!py-0"
        width="6rem"
      >
        <template #default="scope">
          <div class="flex gap-2">
            <user-group-list-actions
              :user-group="scope.row"
              :organization-code="organizationCode"
              @change="reloadWithContext"
            />
          </div>
        </template>
      </lxc-table-column>
    </lxc-table>
  </container-component>

  <user-group-form
    v-model:show="showGroupForm"
    :organization-code="organizationCode"
    :group-name="currentSelectedGroup"
    @close="onCloseCanvas"
  />
</template>

<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
